import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useFinanceAuth } from "@/context/FinanceAuthContext";
import { APP } from "@/constants/testIds";
import api from "@/api";
import {
  LayoutDashboard,
  HardHat,
  Wrench,
  Fingerprint,
  ClipboardList,
  FileBarChart,
  Users,
  Wallet,
  Receipt,
  Calculator,
  Clock,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  BookOpen,
  Lock,
  Calendar as CalendarIcon,
  Palmtree,
  Package,
  Database,
  Search as SearchIcon,
  Send,
  ChevronDown,
  ChevronRight,
  Building2,} from "lucide-react";
import GlobalSearch from "@/components/GlobalSearch";
import { useCompanySettings } from "@/context/CompanySettingsContext";

const CONTABILITA_ITEMS = [
  { to: "/costi-fissi", icon: FileBarChart, label: "Costi di gestione", testId: APP.navCostiFissi },
  { to: "/costi-variabili", icon: Wallet, label: "Costi variabili", testId: "nav-costi-variabili" },
  { to: "/iva", icon: Wallet, label: "IVA (previsionale/effettiva)", testId: "nav-iva" },
  { to: "/contabilita?tab=richieste-fatturazione", icon: Users, label: "Richieste fatturazione", testId: "nav-richieste-fatturazione" },
  { to: "/fatturato-previsionale", icon: Receipt, label: "Fatturato Previsionale", testId: "nav-fatturato-previsionale" },
  { to: "/report-ore", icon: Clock, label: "Report ore e costi lavoratori", testId: APP.navReportOre },
  { to: "/cashflow", icon: Wallet, label: "Cash Flow", testId: APP.navCashflow },
  { to: "/bep", icon: Calculator, label: "BEP", testId: APP.navBep },
  { to: "/situazione", icon: Lock, label: "Situazione economica", testId: "nav-situazione" },
];

const NAV_ADMIN = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testId: APP.navDashboard },
  { to: "/cantieri", icon: HardHat, label: "Cantieri", testId: APP.navCantieri },
  { to: "/manutenzioni", icon: Wrench, label: "Manutenzioni", testId: "nav-manutenzioni" },
  { to: "/opportunita-promemoria", icon: LayoutDashboard, label: "Opportunità e Promemoria", testId: "nav-opportunita-promemoria" },
  { to: "/calendario", icon: CalendarIcon, label: "Calendario", testId: "nav-calendario" },
  { to: "/timbratura", icon: Fingerprint, label: "Timbratura", testId: APP.navTimbratura },
  { to: "/rapportini", icon: ClipboardList, label: "Rapportini", testId: APP.navRapportini },
  { to: "/bolle", icon: Receipt, label: "Bolle & Materiali", testId: APP.navBolle },
  { to: "/fornitori", icon: Building2, label: "Agenda Fornitori", testId: "nav-fornitori" },
  { to: "/magazzino", icon: Package, label: "Magazzino", testId: "nav-magazzino" },
  { to: "/richieste-magazzino", icon: Package, label: "Richieste materiali", testId: "nav-richieste-magazzino" },
  { to: "/lavoratori", icon: Users, label: "Lavoratori", testId: APP.navLavoratori },
  { to: "/resource-pool", icon: Users, label: "Resource Pool", testId: "nav-resource-pool" },
  { to: "/ferie", icon: Palmtree, label: "Ferie & Permessi", testId: "nav-ferie" },
  { group: "contabilita", icon: BookOpen, label: "Contabilità", testId: "nav-contabilita-group", items: CONTABILITA_ITEMS },
  { to: "/utenti", icon: ShieldCheck, label: "Utenti & Ruoli", testId: APP.navUsers },
  { to: "/admin-tools", icon: Database, label: "Backup · Log · Cestino", testId: "nav-admin-tools" },
  { to: "/impostazioni-azienda", icon: Building2, label: "Impostazioni azienda", testId: "nav-impostazioni-azienda" },
];

const NAV_WORKER = [
  { to: "/cantieri", icon: HardHat, label: "I miei Cantieri", testId: APP.navCantieri },
  { to: "/manutenzioni", icon: Wrench, label: "Manutenzioni", testId: "nav-manutenzioni-worker" },
  { to: "/calendario", icon: CalendarIcon, label: "Calendario", testId: "nav-calendario" },
  { to: "/timbratura", icon: Fingerprint, label: "Timbratura", testId: APP.navTimbratura },
  { to: "/rapportini", icon: ClipboardList, label: "Rapportini", testId: APP.navRapportini },
  { to: "/bolle", icon: Receipt, label: "Bolle", testId: APP.navBolle },
  { to: "/magazzino", icon: Package, label: "Magazzino", testId: "nav-magazzino" },
  { to: "/richieste-magazzino", icon: Package, label: "Richieste materiali", testId: "nav-richieste-magazzino" },
  { to: "/ferie", icon: Palmtree, label: "Le mie ferie", testId: "nav-ferie" },
];

function roleLabel(role) {
  const map = {
    amministratore: "Amministratore",
    responsabile_cantiere: "Responsabile Cantiere",
    impiegato_amministrativo: "Impiegato amm.",
    lavoratore: "Lavoratore",
    collaboratore_esterno: "Collaboratore esterno",
  };
  return map[role] || role;
}

function ContabilitaMenu({ item, onNavigate }) {
  const location = useLocation();
  const { unlocked } = useFinanceAuth();
  const isChildActive = item.items.some((sub) => location.pathname.startsWith(sub.to));
  const [openGroup, setOpenGroup] = useState(isChildActive);
  useEffect(() => {
    if (isChildActive) setOpenGroup(true);
  }, [isChildActive]);

  return (
    <div data-testid={item.testId}>
      <button
        type="button"
        onClick={() => setOpenGroup((v) => !v)}
        data-testid="nav-contabilita-toggle"
        aria-expanded={openGroup}
        className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-colors border-l-2 ${
          isChildActive
            ? "border-blue-500 bg-slate-800 text-white"
            : "border-transparent text-slate-300 hover:bg-slate-800 hover:text-white"
        }`}
      >
        <item.icon className="w-4 h-4" />
        <span className="flex-1 text-left">{item.label}</span>
        {unlocked ? (
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" title="Sezione sbloccata" />
        ) : (
          <Lock className="w-3.5 h-3.5 text-slate-500" title="Sezione protetta da password" />
        )}
        {openGroup ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {openGroup && (
        <div className="bg-slate-950/40" data-testid="nav-contabilita-submenu">
          {item.items.map((sub) => (
            <NavLink
              key={sub.to}
              to={sub.to}
              data-testid={sub.testId}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 pl-10 pr-5 py-2 text-xs transition-colors border-l-2 ${
                  isActive
                    ? "border-orange-500 bg-slate-800 text-white"
                    : "border-transparent text-slate-400 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <sub.icon className="w-3.5 h-3.5" />
              {sub.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}


function BozzeBadge() {
  const [n, setN] = useState(0);
  useEffect(() => {
    let stopped = false;
    const fetchIt = () =>
      api.get("/richieste-materiali/count-bozze")
        .then((r) => { if (!stopped) setN(r.data?.count || 0); })
        .catch(() => {});
    fetchIt();
    const t = setInterval(fetchIt, 60_000);
    return () => { stopped = true; clearInterval(t); };
  }, []);
  if (n === 0) return null;
  return (
    <span
      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-rose-600 text-white animate-pulse"
      title={`${n} bozze da confermare (riordino automatico o manuale)`}
      data-testid="badge-bozze-riordino"
    >
      {n}
    </span>
  );
}



function AppVersion() {
  const [v, setV] = useState(null);
  const [updating, setUpdating] = useState(false);
  useEffect(() => {
    api.get("/version").then((r) => setV(r.data)).catch(() => {});
  }, []);

  const forceUpdate = async () => {
    if (updating) return;
    setUpdating(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (_) { /* silent */ }
    window.location.reload();
  };

  if (!v) return null;
  return (
    <div className="mt-3 text-center text-[10px] font-mono text-slate-500 space-y-1" data-testid="app-version">
      <div>Ethos v{v.version} · {v.codename}</div>
      <button
        onClick={forceUpdate}
        disabled={updating}
        data-testid="btn-force-update"
        title="Svuota la cache locale e ricarica l'app all'ultima versione"
        className="mx-auto inline-flex items-center gap-1 px-2 py-1 border border-slate-700 hover:bg-slate-800 text-slate-300 hover:text-white rounded-sm text-[10px] font-semibold uppercase tracking-widest disabled:opacity-50"
      >
        {updating ? "…" : "↻ Aggiorna app"}
      </button>
    </div>
  );
}


export default function Layout() {
  const { user, logout } = useAuth();
  const { company } = useCompanySettings();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const nav = isAdmin(user) ? NAV_ADMIN : NAV_WORKER;

  // ⌘K / Ctrl+K per aprire la ricerca globale
  useEffect(() => {
    const handler = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === "k" || e.key === "K")) {
        // evita di intercettare quando il focus è già dentro un input dedicato
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside
        data-testid={APP.sidebar}
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-slate-900 text-slate-100 flex flex-col z-40 transition-transform lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            {company.logo_base64 ? (
              <img src={company.logo_base64} alt="" className="w-9 h-9 object-contain bg-white rounded-sm p-0.5" />
            ) : (
              <div
                className="w-9 h-9 flex items-center justify-center rounded-sm"
                style={{ background: company.colore_accento || "#f97316" }}
              >
                <span className="font-display font-black text-white text-lg leading-none">
                  {(company.nome_breve || "E")[0].toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <div className="font-display font-bold text-lg leading-tight tracking-wide">
                {company.nome_breve || "ETHOS"}
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.2em] font-semibold"
                style={{ color: company.colore_accento || "#f97316" }}
              >
                {(company.ragione_sociale || "Costruzioni").replace(company.nome_breve || "", "").trim() || "Costruzioni"}
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {nav.map((item) => (
            item.group === "contabilita"
              ? <ContabilitaMenu key={item.group} item={item} onNavigate={() => setOpen(false)} />
              : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-3 text-sm transition-colors border-l-2 ${
                      isActive
                        ? "border-blue-500 bg-slate-800 text-white"
                        : "border-transparent text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  <span className="flex-1">{item.label}</span>
                  {item.to === "/richieste-magazzino" && <BozzeBadge />}
                </NavLink>
              )
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-blue-700 flex items-center justify-center text-sm font-bold">
                {user?.name?.[0] || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-slate-400 truncate">{roleLabel(user?.role)}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid={APP.logoutButton}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm bg-slate-800 hover:bg-slate-700 transition-colors rounded-sm"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
          <AppVersion />
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="gc-sticky sticky top-0 z-20 px-4 lg:px-8 py-3 flex items-center justify-between">
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid={APP.sidebarToggle}
            className="lg:hidden w-9 h-9 flex items-center justify-center border border-slate-300 rounded-sm"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
          <div>
            <div className="overline">Impresa</div>
            <div className="font-display font-semibold">{user?.societa || "Impresa"}</div>
          </div>
          <div className="flex items-center gap-3" data-testid={APP.userMenu}>
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 border border-slate-300 rounded-sm text-xs text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid="global-search-trigger"
              title="Ricerca globale (⌘K)"
            >
              <SearchIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Cerca…</span>
              <kbd className="hidden sm:inline text-[9px] font-mono px-1 py-0.5 border border-slate-300 rounded-sm ml-1">⌘K</kbd>
            </button>
            <span className="hidden md:inline text-sm text-slate-600">{user?.email}</span>
            <span className="px-2 py-1 text-[10px] uppercase font-bold tracking-wider bg-slate-900 text-white rounded-sm">
              {roleLabel(user?.role)}
            </span>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
